import { translate } from '@/plugins/i18n.js'
import type { ApiRequestError } from '@/services/api/AccountApi'
import MessageHelper from '@/services/helpers/MessageHelper'

/** Field name from the API payload -> message to show on that field */
export type ServerErrors = Record<string, string>

const VICAMPO_ERROR_CODE = {
  PASSWORD_TOO_SHORT: 1001,
  EMAIL_ALREADY_EXISTS: 1002,
  EMAIL_INVALID: 1000,
  PASSWORD_INCORRECT: 1013
} as const

const ERROR_MESSAGE_KEYS: Record<number, string> = {
  [VICAMPO_ERROR_CODE.PASSWORD_TOO_SHORT]:
    'account_details_error_password_short',
  [VICAMPO_ERROR_CODE.EMAIL_ALREADY_EXISTS]:
    'account_details_error_email_exists',
  [VICAMPO_ERROR_CODE.EMAIL_INVALID]: 'validation_msg_email',
  [VICAMPO_ERROR_CODE.PASSWORD_INCORRECT]:
    'account_details_error_password_incorrect'
}

/**
 * Only the statuses that really mean "the payload was wrong". A 401/403/404/429 is not
 * something the customer can fix by editing a field, so those keep the generic message
 * and stay reportable. 413 belongs here as well: the profile photo endpoint answers an
 * oversized image with it, and picking a smaller file is exactly the fix.
 */
const VALIDATION_STATUS = new Set([400, 409, 413, 422])

/**
 * Turns an API error into per-field messages. Whatever the API blamed on a field is
 * shown on that field, the rest goes to the flash messenger so the customer is never
 * left with a form that silently did nothing.
 *
 * Only unexpected failures are reported to the backend log: a rejected password, a taken
 * email address or an image that is too large is a normal user error, not an incident
 * (VIP-12034).
 *
 * @param error - the error RequestHelper threw
 * @param source - tag the report is filed under when the failure is unexpected
 * @param fallbackMessageKey - shown when the API blamed no known error code
 * @param unknownMessageKey - flash shown for an unexpected failure (a 500, a dropped
 *   connection); defaults to the generic message. Pass a tab-specific key to tell the
 *   customer which action failed instead.
 */
export const mapApiError = (
  error: ApiRequestError,
  source: string,
  fallbackMessageKey = 'please_check_your_data',
  unknownMessageKey = 'unknown_error'
): ServerErrors => {
  const messageKey = ERROR_MESSAGE_KEYS[Number(error?.cause)]
  const field = error?.field
  const isUserError = VALIDATION_STATUS.has(Number(error?.status))

  if (messageKey || isUserError) {
    // The profile endpoint answers field validation with a 422 and the field name but
    // no error code; the client-side rules already cover those cases in detail.
    const message = translate(messageKey ?? fallbackMessageKey)

    if (field) return { [field]: message }

    void MessageHelper.addErrorMessage(messageKey ?? fallbackMessageKey)
    return {}
  }

  void MessageHelper.addErrorMessage(
    unknownMessageKey,
    null,
    5000,
    error,
    source
  )
  return {}
}
